import React, { JSX } from 'react';
import {
    ImageField,
    Field,
    LinkField,
    Link,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { StyleguideComponentProps } from 'lib/component-props';
import { classNames } from 'assets/utils/helper';
import { NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import { getMarginSize } from 'assets/utils/getMarginSize';

type HeroCampaignProps = StyleguideComponentProps & {
    fields: {
        BackgroundImage: ImageField;
        Tagline: Field<string>;
        Headline: Field<string>;
        CTA: LinkField;
        MarginSize: {
            fields: {
                heading: {
                    value: string;
                };
            };
        };
    };
};

// Accent panel shape used when there is a badge bottom-right corner.
const AccentPanelShapeLarge = (): JSX.Element => (
    <svg
        className="hero-accent-panel-bg hero-accent-panel-bg--large"
        viewBox="0 0 713 320"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M1.5 1.5V318.5H626L711 247V1.5H1.5Z"
            fill="url(#hero-accent-panel-gradient-large)"
            fillOpacity="0.85"
            stroke="#00A9CE"
            strokeWidth="3"
        />
        <defs>
            <linearGradient
                id="hero-accent-panel-gradient-large"
                x1="160"
                y1="713"
                x2="713"
                y2="160"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0.466346" stopColor="#1684C0" stopOpacity="0.62" />
                <stop offset="1" stopColor="#4E8209" stopOpacity="0.62" />
            </linearGradient>
        </defs>
    </svg>
);

const AccentPanelShapeSmall = (): JSX.Element => (
    <svg
        className="hero-accent-panel-bg hero-accent-panel-bg--small"
        viewBox="0 0 713 320"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
    >
        {/* Fill: full closed shape, no stroke */}
        <path
            d="M1.5 1.5V318.5H626L711 247V1.5H1.5Z"
            fill="url(#hero-accent-panel-gradient-small)"
            fillOpacity="0.85"
        />
        {/* Border: open path — bottom, right, diagonal, top. No left edge. */}
        <path
            d="M1.5 318.5H626L711 247V1.5H1.5"
            fill="none"
            stroke="#00A9CE"
            strokeWidth="3"
            vector-effect="non-scaling-stroke"
        />
        <defs>
            <linearGradient
                id="hero-accent-panel-gradient-small"
                x1="160"
                y1="713"
                x2="713"
                y2="160"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0.466346" stopColor="#1684C0" stopOpacity="0.62" />
                <stop offset="1" stopColor="#4E8209" stopOpacity="0.62" />
            </linearGradient>
        </defs>
    </svg>
);

// Accent panel shape used when there is no badge top-right corner.
const AccentPanelShapeNoBadge = (): JSX.Element => (
    <svg
        className="hero-accent-panel-bg"
        viewBox="0 0 713 320"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
    >
        {/* Path for vertical left edge only */}
        <path
            d="M0 0V318.5H710V73L658 1.5H1.5Z"
            fill="url(#hero-accent-panel-gradient-no-badge)"
            fillOpacity="0.85"
        />
        {/* path for all border except the left edge */}
        <path
            d="M0 318.5H710V73L658 1.5H0"
            fill="none"
            stroke="#00A9CE"
            strokeWidth="3"
            vector-effect="non-scaling-stroke"
        />
        <defs>
            <linearGradient
                id="hero-accent-panel-gradient-no-badge"
                x1="160"
                y1="713"
                x2="713"
                y2="160"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0.466346" stopColor="#1684C0" stopOpacity="0.62" />
                <stop offset="1" stopColor="#4E8209" stopOpacity="0.62" />
            </linearGradient>
        </defs>
    </svg>
);

const BadgeShape = (): JSX.Element => (
    <svg
        className="hero-badge-bg"
        viewBox="0 0 251 82"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M249.5 1.5V80.5H22.5752L1.5 58.5127V1.5H249.5Z"
            fill="white"
            stroke="#00A9CE"
            strokeWidth="3"
            vector-effect="non-scaling-stroke"
        />
    </svg>
);

const HeroCampaign = (props: HeroCampaignProps): JSX.Element => {
    if (!props || !props.fields) {
        return <div></div>;
    }

    const marginSize = getMarginSize(
        props?.fields?.MarginSize?.fields?.heading?.value
    );

    const hasTagline = Boolean(props.fields.Tagline?.value);

    return (
        <div
            className={classNames('overhauled', marginSize)}
            id={`hero-${props.rendering?.uid}`}
        >
            <div className={classNames('hero-campaign')}>
                {props.fields.BackgroundImage?.value?.src && (
                    <NextImage
                        field={props.fields.BackgroundImage}
                        alt={
                            (props.fields.BackgroundImage.value?.alt as string) != ''
                                ? (props.fields.BackgroundImage.value?.alt as string)
                                : 'Image'
                        }
                        className="hero-img"
                    />
                )}
                <div className="hero-inner-wrapper">
                    <div className="jss-inner-container">
                        <div
                            className={classNames(
                                    'hero-campaign-content',
                                    !hasTagline ? 'hero-badge-no' : ''
                                )}>
                            <div className="hero-badge">
                                <BadgeShape />
                                <span className="osf-display-h3 hero-badge-text">
                                    {props.fields.Tagline?.value}
                                </span>
                            </div>
                            <div className="hero-accent-panel">
                                {hasTagline ? (
                                    <>
                                        <AccentPanelShapeSmall />
                                        <AccentPanelShapeLarge />
                                    </>
                                ) : (
                                    <AccentPanelShapeNoBadge />
                                )}
                                <div className="hero-accent-panel-content">
                                    {props.fields.Headline?.value && (
                                        <h1 className="osf-headline-h2 hero-description">
                                            {props.fields.Headline?.value}
                                        </h1>
                                    )}
                                    {props.fields.CTA?.value?.href &&
                                        props.fields.CTA?.value.href !== '' && (
                                            <Link
                                                className={classNames(`btn-secondary`)}
                                                field={props.fields.CTA}
                                            />
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeroCampaign;
